import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { MicOff, VideoOff } from 'lucide-react';

/**
 * Video tile ligado diretamente aos objetos Track do livekit-client.
 *
 * Em vez de receber um MediaStream e setar srcObject manualmente (o que quebra
 * em iOS Safari apos troca de rede porque o <video> fica segurando um track
 * morto), recebe os proprios Track do LiveKit e usa track.attach(element).
 * Esse metodo do SDK:
 *   - Liga o MediaStreamTrack atual ao <video>, autoplay + playsInline
 *   - Reatach automatico se o MediaStreamTrack interno for substituido
 *     (acontece em ICE restart depois de wifi<->4G)
 *   - Cleanup correto via detach(element) no unmount
 *
 * Props:
 *   videoTrack - VideoTrack do LiveKit (local ou remote). Pode ser null.
 *   audioTrack - AudioTrack do LiveKit (so remoto, self-view nao reproduz
 *                audio local pra evitar echo). Pode ser null.
 *   muted      - controla playback local do <video>; diferente de micOn (que
 *                e o estado publicado pelo participante remoto)
 *   label      - nome mostrado no canto
 *   micOn      - mic do participante fonte ativo? mostra badge off-air
 *   camOn      - camera do participante fonte ativa? mostra avatar placeholder
 *   mirror     - aplica scaleX(-1) no <video>. Usado no tile remoto pra
 *                espelhar a pessoa que o outro lado publica. Pra nao inverter
 *                texto/logo baked no VirtualBackground, a imagem de fundo e
 *                pre-flipada em flipImage.js antes de ir pro processor - o
 *                scaleX aqui cancela e o texto volta a ficar legivel.
 *   autoPip    - liga o atributo HTML autoPictureInPicture. Chrome/Edge
 *                movem o <video> pra PiP automaticamente quando a aba perde
 *                visibilidade. Declarativo - nao exige user-gesture. Safari
 *                ignora o atributo mas tem comportamento proprio de auto-PiP.
 */
const VideoPlayer = forwardRef(function VideoPlayer({
  videoTrack,
  audioTrack,
  muted = false,
  label,
  className = '',
  micOn = true,
  camOn = true,
  mirror = false,
  autoPip = false,
  // Contador que bumpa quando o room faz Reconnected/SignalReconnected.
  // Usado como dep do useEffect de reattach - forca um track.attach(el) +
  // play() sempre que o SDK restaurou a sessao, pra desempacar o decoder
  // que as vezes fica pausado mesmo com MediaStreamTrack vivo.
  reconnectEpoch = 0,
  // Texto do placeholder quando nao ha video (track nao chegou). Default
  // generico "Aguardando..." pra back-compat. EmbedPage passa textos
  // especificos por contexto: paciente vendo o tile do medico recebe
  // "Aguarde enquanto medico entra na sala...", medico vendo o tile do
  // paciente recebe "Aguardando paciente...".
  placeholderText,
  // Indica que o participante remoto ja entrou na sala, mesmo que ainda
  // nao tenha publicado nenhum track. Cobre o caso do paciente que nega
  // permissao de camera/microfone no browser: o LiveKit aceita a conexao
  // dele, mas ele nao publica nada. Sem esse sinal, o tile do medico
  // ficava preso em "Aguardando paciente..." (porque a logica anterior
  // usava existencia de track como proxy de presenca), enquanto o paciente
  // ja escutava e via o medico - o medico achava que ele nao tinha
  // entrado. Quando true, mostramos label + badge de mic-off + avatar
  // placeholder ("Camera desativada") pra deixar claro que a pessoa esta
  // na sala mas sem midia. So-self-view nao precisa setar.
  participantConnected = false,
}, ref) {
  const videoRef = useRef(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !videoTrack) return undefined;
    // Limpa srcObject antes do attach: em alguns browsers (iOS Safari, Chrome
    // Android em 3G apos switch de rede) o decoder fica preso no ultimo frame
    // do MediaStream anterior mesmo quando attach() aponta pra um novo. Um
    // srcObject=null explicito forca o decoder a resetar antes de receber o
    // stream novo. Custo: um lampejo preto de 1 frame - aceitavel em troca de
    // video nao travado.
    try { el.srcObject = null; } catch { /* ja limpo */ }
    videoTrack.attach(el);
    return () => {
      try { videoTrack.detach(el); } catch { /* elemento ja desmontou */ }
    };
  }, [videoTrack]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !audioTrack) return undefined;
    audioTrack.attach(el);
    return () => {
      try { audioTrack.detach(el); } catch { /* elemento ja desmontou */ }
    };
  }, [audioTrack]);

  // Atributo autoPictureInPicture setado via DOM pra evitar warning do React
  // (nao e um attr padronizado). Quando presente, Chrome/Edge movem o video
  // pra PiP automaticamente quando a aba perde visibilidade.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (autoPip) el.setAttribute('autopictureinpicture', '');
    else el.removeAttribute('autopictureinpicture');
  }, [autoPip]);

  // Recupera o <video> depois de eventos que costumam deixar ele num estado
  // "decoder pausado / srcObject zumbi" mesmo com o track vivo:
  //   1. Fechar Picture-in-Picture (Chrome desktop).
  //   2. Voltar pra aba oculta (Chrome/Safari pausam o decode). Em iOS Safari
  //      o MediaStreamTrack local ate e encerrado; useLiveKit reinicia o
  //      track, e aqui a gente so reataca no <video> e chama play().
  //   3. bfcache restore (pageshow com persisted=true): a pagina foi "congelada"
  //      e restaurada, mas os <video> tracks podem ter ficado inativos.
  //   4. Track 'unmuted' / 'restarted' do LiveKit: servidor informa que o
  //      track remoto voltou a produzir frames (troca wifi<->4G do peer,
  //      ICE restart). Sem listener, o <video> fica congelado no ultimo
  //      frame ate o usuario dar refresh.
  //   5. reconnectEpoch bump: room.Reconnected/SignalReconnected. Mesmo
  //      quando o Track object e o mesmo e o MediaStreamTrack interno esta
  //      vivo, as vezes o decoder do <video> nao retoma - reattach + play
  //      destrava.
  // Re-atacar preserva video + audio no mesmo element.srcObject.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;
    const reattach = () => {
      try { if (videoTrack) videoTrack.attach(el); } catch { /* track pode ter morrido */ }
      try { if (audioTrack) audioTrack.attach(el); } catch { /* idem */ }
      const p = el.play?.();
      if (p?.catch) p.catch(() => { /* autoplay bloqueado - usuario vai destravar */ });
    };
    const handleVisibility = () => { if (!document.hidden) reattach(); };
    const handlePageShow = (e) => { if (e.persisted) reattach(); };
    el.addEventListener('leavepictureinpicture', reattach);
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pageshow', handlePageShow);

    // Listeners no proprio Track do LiveKit. 'unmuted' dispara quando o
    // servidor detecta frames fluindo de novo apos silencio; 'restarted'
    // (quando suportado pelo SDK) dispara quando o MediaStreamTrack interno
    // foi trocado. Ambos indicam "pode chamar play() agora".
    const bindTrackEvents = (track) => {
      if (!track) return () => {};
      const handlers = [];
      const bind = (evt) => {
        try { track.on?.(evt, reattach); handlers.push(() => track.off?.(evt, reattach)); }
        catch { /* SDK antigo sem suporte ao evento */ }
      };
      bind('unmuted');
      bind('restarted');
      return () => handlers.forEach(fn => { try { fn(); } catch {} });
    };
    const unbindVideo = bindTrackEvents(videoTrack);
    const unbindAudio = bindTrackEvents(audioTrack);

    return () => {
      el.removeEventListener('leavepictureinpicture', reattach);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pageshow', handlePageShow);
      unbindVideo();
      unbindAudio();
    };
  }, [videoTrack, audioTrack]);

  // Reattach forcado quando o room reconectou. Roda DEPOIS do useEffect acima
  // porque pode ser que o track object seja o mesmo (ICE restart) e os
  // listeners de unmuted/restarted nao disparem. Chamar attach novamente com
  // o mesmo elemento e idempotente no SDK. Guardamos os tracks em refs pra
  // que o effect dependa SO do reconnectEpoch - nao precisa re-rodar quando
  // o track mudar (isso ja e coberto pelos effects de attach/detach acima).
  const videoTrackRef = useRef(videoTrack);
  const audioTrackRef = useRef(audioTrack);
  videoTrackRef.current = videoTrack;
  audioTrackRef.current = audioTrack;
  useEffect(() => {
    if (reconnectEpoch === 0) return;
    const el = videoRef.current;
    if (!el) return;
    // Reset do srcObject e crucial aqui: a causa mais comum de "video
    // congelado pos-reconnect" e o decoder preso no ultimo frame do
    // MediaStream antigo. Forcar null -> attach faz o browser descartar o
    // stream stale e comecar limpo quando o novo keyframe chegar (o
    // resubscribeAllRemotes em useLiveKit ja pediu keyframe ao servidor).
    try { el.srcObject = null; } catch { /* ja limpo */ }
    try { if (videoTrackRef.current) videoTrackRef.current.attach(el); } catch { /* track pode ter morrido */ }
    try { if (audioTrackRef.current) audioTrackRef.current.attach(el); } catch { /* idem */ }
    const p = el.play?.();
    if (p?.catch) p.catch(() => { /* autoplay bloqueado */ });
  }, [reconnectEpoch]);

  // PiP manual: pai dispara togglePip() via ref. Flipa o estado - se ja esta
  // em PiP, fecha; senao, abre. No modo mirror a janela do PiP sai sem o
  // scaleX(-1) (Chrome nao propaga CSS transform pro PiP); trade-off aceito
  // em troca de simplicidade e estabilidade.
  useImperativeHandle(ref, () => ({
    async togglePip() {
      const video = videoRef.current;
      if (!video) return;
      if (document.pictureInPictureElement === video) {
        try { await document.exitPictureInPicture(); } catch { /* ja fechou */ }
        return;
      }
      try {
        await video.requestPictureInPicture();
      } catch (err) {
        console.warn('PiP request failed:', err);
      }
    },
    isPipActive() {
      return document.pictureInPictureElement === videoRef.current;
    },
  }), []);

  const initials = (label || '?')
    .trim()
    .split(/\s+/)
    .map(p => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const hasVideo = !!videoTrack;
  // "Pessoa ja chegou no tile" = ou ja publicou alguma midia, ou o caller
  // confirmou explicitamente que o participante esta conectado na sala.
  // Os dois caminhos cobrem situacoes diferentes:
  //  - hasAnyTrack: caso comum, participante entrou e publicou camera/mic.
  //    Tambem cobre self-view (que nao recebe participantConnected).
  //  - participantConnected: paciente entrou na sala mas negou permissao
  //    de camera/microfone (ou esta no device-check). Sem esse sinal, o
  //    doutor via "Aguardando paciente..." indefinidamente enquanto o
  //    paciente ja ouvia e via o doutor.
  // O gate substitui o `hasAnyTrack` puro que estava aqui antes - aquele
  // resolvia a janela de loading mas criava o bug de paciente-sem-permissao
  // ficar invisivel pro doutor.
  const participantPresent = participantConnected || !!(videoTrack || audioTrack);

  return (
    <div className={`relative bg-gray-900 rounded-xl overflow-hidden ${className}`}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={muted}
        // object-contain keeps the full 16:9 frame visible instead of cropping,
        // important here because doctors rely on reading nome+CRM baked into
        // the VirtualBackground image which often lives near the top.
        className={`w-full h-full object-contain bg-black ${camOn ? '' : 'invisible'}`}
        style={mirror ? { transform: 'scaleX(-1)' } : undefined}
      />

      {/* Camera off: avatar placeholder takes over the tile so the other side
          sees something deliberate instead of a black rectangle. Cobre dois
          casos: (a) participante publicou camera mas mutou mid-call - cam
          off real, (b) paciente entrou na sala sem nenhum track de video
          (negou permissao no browser ou device-check em loop) - sem isso
          ele cairia no placeholder "Aguardando..." e o medico achava que
          ele nem tinha entrado. */}
      {participantPresent && (!hasVideo || !camOn) && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
          <div className="text-center text-slate-300">
            <div className="w-24 h-24 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3 text-2xl font-semibold">
              {initials}
            </div>
            <div className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
              <VideoOff size={14} />
              <span>Camera desativada</span>
            </div>
          </div>
        </div>
      )}

      {/* Mic off badge - so aparece quando o participante ja esta presente
          (publicou track ou caller confirmou conexao). Sem isso, doutor via
          badge mutado pra paciente que ainda tava no proprio loading. */}
      {!micOn && participantPresent && (
        <div
          className="absolute top-2 right-2 bg-rose-600 text-white p-1.5 rounded-full shadow-lg ring-2 ring-rose-900/60 flex items-center gap-1 z-10"
          title="Microfone desativado"
          aria-label="Microfone desativado"
        >
          <MicOff size={14} />
        </div>
      )}

      {label && participantPresent && (
        <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded flex items-center gap-1.5">
          <span>{label}</span>
          {!micOn && <MicOff size={11} className="text-rose-400" aria-hidden />}
        </div>
      )}

      {/* "Aguardando..." so quando ninguem chegou ainda. Quando o participante
          esta presente sem video, o avatar placeholder acima ja toma a tela. */}
      {!participantPresent && !hasVideo && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-gray-400 px-4">
            <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-2">
              <span className="text-2xl">👤</span>
            </div>
            <p className="text-sm">{placeholderText || 'Aguardando...'}</p>
          </div>
        </div>
      )}
    </div>
  );
});

export default VideoPlayer;
